import React, {useEffect, useState} from 'react';
import {CAlert, CButton, CInput, CModal, CModalBody, CModalFooter, CModalHeader, CModalTitle} from '@coreui/react';
import {getApi} from "../../apis/backendApis";
import {Fluxo, Previa, formataData, paraApi} from "./modelo";

interface Props {
    fluxo: Fluxo,
    onFechar: () => void,
}

const DIA = 24 * 60 * 60 * 1000;
const hoje = () => {
    const agora = new Date();
    const doisDigitos = (n: number) => String(n).padStart(2, '0');
    return `${agora.getFullYear()}-${doisDigitos(agora.getMonth() + 1)}-${doisDigitos(agora.getDate())}`;
};
const emDias = (data: string) => Math.round(new Date(`${data}T00:00:00Z`).getTime() / DIA);

/**
 * Simula o cronograma do fluxo em edicao (salvo ou nao) a partir de uma data,
 * em dias uteis: e o que a abertura do processo vai gerar.
 */
const PreviaModal = ({fluxo, onFechar}: Props) => {
    const [inicio, setInicio] = useState(hoje());
    const [previa, setPrevia] = useState<Previa | null>(null);
    const [erro, setErro] = useState<string | null>(null);
    const [carregando, setCarregando] = useState(false);
    const [aberta, setAberta] = useState<number | null>(null);

    useEffect(() => {
        if (!/^\d{4}-\d{2}-\d{2}$/.test(inicio)) {
            return;
        }
        const corpo = new FormData();
        corpo.append('data', JSON.stringify(paraApi(fluxo)));
        corpo.append('inicio', inicio);
        setCarregando(true);
        setErro(null);
        getApi().post('fluxo/cronograma-previa', corpo)
            .then(resposta => setPrevia(resposta.data))
            .catch(e => {
                setPrevia(null);
                setErro(String(e?.response?.data?.error ?? 'Não foi possível calcular o cronograma.'));
            })
            .finally(() => setCarregando(false));
    }, [inicio]);

    const escala = previa ? {de: emDias(previa.inicio), dias: emDias(previa.fim) - emDias(previa.inicio) + 1} : null;
    const barra = (de: string, ate: string) => {
        if (!escala) {
            return {};
        }
        const esquerda = (emDias(de) - escala.de) / escala.dias;
        const largura = (emDias(ate) - emDias(de) + 1) / escala.dias;
        return {left: `${esquerda * 100}%`, width: `${Math.max(largura * 100, 0.8)}%`};
    };

    return (
        <CModal show onClose={onFechar} size="lg" className="fx-previa">
            <CModalHeader closeButton>
                <CModalTitle>Prévia do cronograma</CModalTitle>
            </CModalHeader>
            <CModalBody>
                <div className="fx-previa-topo">
                    <label htmlFor="previa-inicio" className="mb-0">Início do processo</label>
                    <CInput id="previa-inicio" type="date" size="sm" value={inicio} onChange={(e: any) => setInicio(e.target.value)}/>
                    {previa && (
                        <span className="fx-previa-total">
                            Término em <strong>{formataData(previa.fim)}</strong> · {previa.dias_uteis} dias úteis
                        </span>
                    )}
                    {carregando && <span className="text-muted">calculando…</span>}
                </div>

                {erro && <CAlert color="danger" className="mt-3 fx-erro-texto">{erro}</CAlert>}
                {previa?.aviso && <CAlert color="warning" className="mt-3">{previa.aviso}</CAlert>}

                {previa && (
                    <div className="fx-gantt">
                        {previa.etapas.map(etapa => (
                            <div key={etapa.indice} className="fx-gantt-etapa">
                                <button type="button" className="fx-gantt-linha" onClick={() => setAberta(aberta === etapa.indice ? null : etapa.indice)}>
                                    <span className="fx-gantt-nome">{etapa.nome}</span>
                                    <span className="fx-gantt-trilho">
                                        <span className="fx-gantt-barra" style={barra(etapa.inicio, etapa.fim)}/>
                                    </span>
                                    <span className="fx-gantt-datas">{formataData(etapa.inicio)} – {formataData(etapa.fim)}</span>
                                </button>
                                {aberta === etapa.indice && (
                                    <ul className="fx-gantt-itens">
                                        {etapa.itens.map(item => (
                                            <li key={item.indice}>
                                                <span>{item.titulo}</span>
                                                <span className="text-muted">{formataData(item.prazo)}</span>
                                            </li>
                                        ))}
                                        {etapa.itens.length === 0 && <li className="text-muted">Sem itens ativos.</li>}
                                    </ul>
                                )}
                            </div>
                        ))}
                        {previa.etapas.length === 0 && <div className="text-muted mt-3">Nenhuma etapa ativa.</div>}
                    </div>
                )}
                <small className="text-muted d-block mt-3">
                    Dias úteis: segunda a sexta, menos os feriados cadastrados. Clique numa etapa para ver o prazo de cada item.
                </small>
            </CModalBody>
            <CModalFooter>
                <CButton color="secondary" size="sm" onClick={onFechar}>Fechar</CButton>
            </CModalFooter>
        </CModal>
    );
};

export default PreviaModal;
